import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getOneEvents, updateEvents } from 'slice/eventsSlice';
import { toast } from 'react-toastify';
import { useParams, useNavigate } from 'react-router-dom';

const getEventMediaUrl = (mediaPath) => {
    if (!mediaPath) return null;
    if (/^https?:\/\//i.test(mediaPath)) return mediaPath;
    const base = (process.env.REACT_APP_BACKEND || '').replace(/\/+$/, '');
    const normalized = String(mediaPath).replace(/^\/+/, '');
    if (normalized.startsWith('uploads/')) {
        return `${base}/${normalized}`;
    }
    return `${base}/uploads/${normalized}`;
};

const UpdateEvent = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { loading, data, error } = useSelector((state) => state.events);
    const [formData, setFormData] = useState({
        heading: '',
        sub_heading: '',
        image: null,
        file: null,
        is_active: 1
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const prevLoadingRef = useRef(null);

    useEffect(() => {
        dispatch(getOneEvents(id));
    }, [dispatch, id]);

    useEffect(() => {
        if (data) {
            setFormData({
                heading: data.heading || '',
                sub_heading: data.sub_heading || '',
                is_active: data.is_active ?? 1,
                image: null,
                file: null
            });
        }
    }, [data]);

    useEffect(() => {
        if (prevLoadingRef.current === null) {
            prevLoadingRef.current = loading;
            return;
        }

        const wasLoading = prevLoadingRef.current;
        prevLoadingRef.current = loading;

        if (isSubmitting && wasLoading === true && loading === false) {
            setIsSubmitting(false);
            if (!error) {
                toast.success('Event updated successfully!');
                navigate('/events');
            } else {
                toast.error(typeof error === 'string' ? error : 'Failed to update event');
            }
        }
    }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: type === 'file' ? e.target.files[0] : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const formDataToSend = {
            heading: formData.heading,
            sub_heading: formData.sub_heading || '',
            is_active: formData.is_active,
            image: formData.image || null,
            file: formData.file || null
        };

        setIsSubmitting(true);
        dispatch(updateEvents({ id: id, data: formDataToSend }))
    };

    return (
        <div className="container-fluid px-4 py-4">
            <div className="card shadow border-0">
                <div className="card-header bg-primary text-white py-3">
                    <h5 className="mb-0">Update Event</h5>
                </div>
                <div className="card-body p-4">
                    <form onSubmit={handleSubmit}>
                        <div className="row">
                            <div className="col-md-6 mb-4">
                                <label htmlFor="eventHeading" className="adminlables">Event Heading</label>
                                <input
                                    type="text"
                                    id="eventHeading"
                                    name="heading"
                                    value={formData.heading}
                                    className="form-control"
                                    placeholder="Update heading"
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                            <div className="col-md-6 mb-4">
                                <label htmlFor="eventSubHeading" className="adminlables">Sub Heading</label>
                                <input
                                    type="text"
                                    id="eventSubHeading"
                                    name="sub_heading"
                                    value={formData.sub_heading}
                                    className="form-control"
                                    placeholder="Update sub heading"
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="col-md-6 mb-4">
                                <label htmlFor="is_active" className="adminlables">Status</label>
                                <select id="is_active" name="is_active" value={formData.is_active} className="form-control" onChange={handleChange}>
                                    <option value={1}>Active</option>
                                    <option value={0}>Inactive</option>
                                </select>
                            </div>
                            <div className="col-md-6 mb-4">
                                <label htmlFor="eventImage" className="adminlables">Event Image (Leave blank to keep current)</label>
                                <input
                                    type="file"
                                    id="eventImage"
                                    name="image"
                                    accept="image/*"
                                    className="form-control btn-block filebg"
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="col-md-6 mb-4">
                                <label htmlFor="eventFile" className="adminlables">Attachment File (Leave blank to keep current)</label>
                                <input
                                    type="file"
                                    id="eventFile"
                                    name="file"
                                    accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.zip,.ppt,.pptx"
                                    className="form-control btn-block filebg"
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="col-md-6 mb-4">
                                <p className="adminlables mb-1">Current Image</p>
                                <div>
                                    {data?.image ? (
                                        <img
                                            src={getEventMediaUrl(data.image)}
                                            alt={data.heading}
                                            className="banner-thumb"
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                    ) : (
                                        <p className="text-muted mb-0">No image</p>
                                    )}
                                </div>
                            </div>
                            <div className="col-md-6 mb-4">
                                <p className="adminlables mb-1">Current File</p>
                                <div>
                                    {data?.file ? (
                                        <a
                                            href={getEventMediaUrl(data.file)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn btn-sm btn-outline-primary"
                                        >
                                            View Current File
                                        </a>
                                    ) : (
                                        <p className="text-muted mb-0">No file</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="d-flex justify-content-end gap-3 mt-4">
                            <button type="button" className="btn btn-light px-4" onClick={() => navigate('/events')} disabled={isSubmitting}>
                                Cancel
                            </button>
                            <button type="submit" className="btn btn-primary px-5" disabled={isSubmitting}>
                                {isSubmitting ? 'Updating...' : 'Update Event'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    )
}

export default UpdateEvent;
